import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { MessageCircle, Send, Loader2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export const ReformulateAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const { plan, user } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    // Check usage limits if user is authenticated
    if (user) {
      const { data: limitCheck, error: limitError } = await supabase.functions.invoke('check-usage-limits', {
        body: { action: 'reformulate' },
      });

      if (limitError) {
        console.error('Error checking limits:', limitError);
      } else if (limitCheck && !limitCheck.canPerform) {
        toast({
          title: t.limitReached,
          description: limitCheck.message,
          variant: 'destructive',
          action: plan === 'free' ? (
            <Button variant="outline" size="sm" onClick={() => {
              setIsOpen(false);
              navigate('/pricing');
            }}>
              {t.viewPlans}
            </Button>
          ) : undefined,
        });
        return;
      }
    }

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('reformulate-assistant', {
        body: {
          message: input,
          conversationHistory: messages,
          language: language,
        },
      });

      if (error) {
        if (error.message?.includes('429')) {
          toast({
            title: language === 'es' ? 'Límite alcanzado' : language === 'en' ? 'Rate limit reached' : 'Límit assolit',
            description: language === 'es' ? 'Demasiadas solicitudes. Intenta de nuevo en unos minutos.' : language === 'en' ? 'Too many requests. Try again in a few minutes.' : 'Massa sol·licituds. Prova de nou en uns minuts.',
            variant: 'destructive',
          });
        } else if (error.message?.includes('402')) {
          toast({
            title: language === 'es' ? 'Sin créditos' : language === 'en' ? 'No credits' : 'Sense crèdits',
            description: language === 'es' ? 'Por favor añade créditos a tu workspace.' : language === 'en' ? 'Please add credits to your workspace.' : 'Si us plau afegeix crèdits al teu workspace.',
            variant: 'destructive',
          });
        } else {
          throw error;
        }
        return;
      }

      const assistantMessage: Message = { role: 'assistant', content: data.message };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error calling assistant:', error);
      toast({
        title: language === 'es' ? 'Error' : language === 'en' ? 'Error' : 'Error',
        description: language === 'es' ? 'No se pudo obtener respuesta del asistente' : language === 'en' ? 'Could not get response from assistant' : 'No s\'ha pogut obtenir resposta de l\'assistent',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearConversation = () => {
    setMessages([]);
  };

  const assistantTitle = language === 'es' ? 'Asistente de Reformulación' : language === 'en' ? 'Reformulation Assistant' : 'Assistent de Reformulació';
  const placeholderText = language === 'es' ? 'Escribe tu afirmación médica aquí...' : language === 'en' ? 'Write your medical statement here...' : 'Escriu la teva afirmació mèdica aquí...';
  const clearText = language === 'es' ? 'Limpiar' : language === 'en' ? 'Clear' : 'Netejar';

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-shadow z-50"
          aria-label={assistantTitle}
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:w-[440px] flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl">{assistantTitle}</SheetTitle>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearConversation}>
                <X className="h-4 w-4 mr-1" />
                {clearText}
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-6" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">
                {language === 'es' 
                  ? 'Pregúntame cómo mejorar tu afirmación médica para obtener mejores resultados de búsqueda.'
                  : language === 'en'
                  ? 'Ask me how to improve your medical statement to get better search results.'
                  : 'Pregunta\'m com millorar la teva afirmació mèdica per obtenir millors resultats de cerca.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="text-sm prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                            ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-4 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="p-6 pt-4 border-t">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholderText}
              className="min-h-[60px] resize-none"
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="icon"
              className="h-[60px] w-[60px] shrink-0"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
